import { DEFAULT_VLM_SETTINGS, VlmSettings, VlmSettingsKey } from "../VlmTabToggles";
import { VlmCaseValueMap } from "./vlmCaseRegistry";
import { VlmConfigPatchBody, VlmConfigResponse } from "./vlmConfigTypes";

export type VlmUiState = {
  settings: VlmSettings;
  customAnalysisName: string;
};

type UiFlagMapping = {
  settingsKey: VlmSettingsKey;
  modality: string;
};

const UI_FLAG_MAPPINGS: UiFlagMapping[] = [
  { settingsKey: "environmentalHazards", modality: "single_image" },
  { settingsKey: "lighting", modality: "single_image" },
  { settingsKey: "entryway", modality: "single_image" },
  { settingsKey: "lossPrevention", modality: "image_diff" },
  { settingsKey: "leftObjectDetection", modality: "image_diff" },
  { settingsKey: "customAnalysis", modality: "single_image" },
];

const getCaseKey = (
  settingsKey: VlmSettingsKey,
  caseValueMap: VlmCaseValueMap
): string | undefined => caseValueMap[settingsKey];

const getFlagEnabled = (
  response: VlmConfigResponse,
  modality: string,
  caseKey: string
): boolean => {
  const entry = response.vlm_flags?.[modality]?.[caseKey];
  return entry?.enabled ?? false;
};

export const mapVlmConfigToUi = (
  response: VlmConfigResponse,
  caseValueMap: VlmCaseValueMap = {}
): VlmUiState => {
  const settings = { ...DEFAULT_VLM_SETTINGS };

  UI_FLAG_MAPPINGS.forEach((mapping) => {
    const caseKey = getCaseKey(mapping.settingsKey, caseValueMap);
    settings[mapping.settingsKey] = caseKey
      ? getFlagEnabled(response, mapping.modality, caseKey)
      : false;
  });

  return {
    settings,
    customAnalysisName: response.vlm_custom_case ?? "",
  };
};

export const buildVlmConfigPatch = (
  baseline: VlmUiState,
  draft: VlmUiState,
  caseValueMap: VlmCaseValueMap = {}
): VlmConfigPatchBody | null => {
  const patch: VlmConfigPatchBody = {};
  const vlmFlags: Record<string, Record<string, boolean>> = {};

  UI_FLAG_MAPPINGS.forEach((mapping) => {
    const baselineValue = baseline.settings[mapping.settingsKey];
    const draftValue = draft.settings[mapping.settingsKey];
    const caseKey = getCaseKey(mapping.settingsKey, caseValueMap);

    if (caseKey && baselineValue !== draftValue) {
      if (!vlmFlags[mapping.modality]) {
        vlmFlags[mapping.modality] = {};
      }
      vlmFlags[mapping.modality][caseKey] = draftValue;
    }
  });

  if (Object.keys(vlmFlags).length > 0) {
    patch.vlm_flags = vlmFlags;
  }

  if (baseline.customAnalysisName !== draft.customAnalysisName) {
    patch.vlm_custom_case = draft.customAnalysisName;
  }

  return Object.keys(patch).length > 0 ? patch : null;
};
